/**
 * Slot arithmetic — the foundation of the whole time model.
 *
 * Time is stored as absolute 30-minute slot indices. A day always consists of
 * exactly 48 slots, as required by the specification ("Tag = 48 Slots",
 * "Gesamt: 31 Tage · 744 h"). Slots therefore describe local *wall-clock* time
 * and deliberately ignore daylight-saving transitions: on the short March day
 * the app still shows 48 half-hours, which is what the design reference does
 * and what users expect from a care schedule.
 *
 * Day indices are counted from 1970-01-01 using `Date.UTC` on the local
 * calendar date. That keeps the arithmetic free of timezone and DST effects —
 * the timezone only enters when translating "now" into a slot.
 */

export const SLOTS_PER_DAY = 48;
export const MINUTES_PER_SLOT = 30;
export const MINUTES_PER_DAY = SLOTS_PER_DAY * MINUTES_PER_SLOT;
export const MS_PER_DAY = 86_400_000;

/** Lowest slot the API accepts: 1970-01-01, 00:00. */
export const MIN_SLOT = 0;
/** Highest slot the API accepts: 2200-01-01, 00:00 — a sanity bound, not a product limit. */
export const MAX_SLOT = 84_006 * SLOTS_PER_DAY;

export interface CalendarDate {
  year: number;
  /** 1-based, January = 1. */
  month: number;
  /** 1-based day of month. */
  day: number;
}

/** Half-open slot interval `[fromSlot, toSlot)`. */
export interface SlotRange {
  fromSlot: number;
  toSlot: number;
}

/** Day index (days since 1970-01-01) of a local calendar date. */
export function dayFromLocalDate(year: number, month: number, day: number): number {
  return Math.floor(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}

/** Inverse of {@link dayFromLocalDate}. */
export function localDateFromDay(dayIndex: number): CalendarDate {
  const date = new Date(dayIndex * MS_PER_DAY);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

export function slotFromLocalDate(year: number, month: number, day: number, slotInDay = 0): number {
  return dayFromLocalDate(year, month, day) * SLOTS_PER_DAY + slotInDay;
}

export function dayOfSlot(slot: number): number {
  return Math.floor(slot / SLOTS_PER_DAY);
}

/** Position of a slot inside its day, `0 … 47`. */
export function slotOfDay(slot: number): number {
  return slot - dayOfSlot(slot) * SLOTS_PER_DAY;
}

export function dayStartSlot(dayIndex: number): number {
  return dayIndex * SLOTS_PER_DAY;
}

export function dayEndSlot(dayIndex: number): number {
  return (dayIndex + 1) * SLOTS_PER_DAY;
}

/**
 * Weekday of a day index with Monday as 0 — the week starts on Monday
 * throughout the app. 1970-01-01 was a Thursday, hence the offset of 3.
 */
export function weekdayOfDay(dayIndex: number): number {
  return (((dayIndex + 3) % 7) + 7) % 7;
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Shifts a year/month pair by whole months, normalising across year borders. */
export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const zeroBased = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (((zeroBased % 12) + 12) % 12) + 1 };
}

/** All slots of one calendar month. */
export function monthSlotRange(year: number, month: number): SlotRange {
  return {
    fromSlot: slotFromLocalDate(year, month, 1),
    toSlot: slotFromLocalDate(year, month, daysInMonth(year, month)) + SLOTS_PER_DAY,
  };
}

/** All slots of one calendar year. */
export function yearSlotRange(year: number): SlotRange {
  return { fromSlot: slotFromLocalDate(year, 1, 1), toSlot: slotFromLocalDate(year + 1, 1, 1) };
}

/**
 * The month grid shown in the month view: full weeks from the Monday of the
 * week containing the 1st through the Sunday of the week containing the last
 * day. The extra days belong to the neighbouring months and are rendered
 * dimmed and non-interactive.
 */
export function monthGridDayRange(year: number, month: number): { firstDay: number; lastDay: number } {
  const first = dayFromLocalDate(year, month, 1);
  const last = dayFromLocalDate(year, month, daysInMonth(year, month));
  return {
    firstDay: first - weekdayOfDay(first),
    lastDay: last + (6 - weekdayOfDay(last)),
  };
}

export function monthGridSlotRange(year: number, month: number): SlotRange {
  const { firstDay, lastDay } = monthGridDayRange(year, month);
  return { fromSlot: dayStartSlot(firstDay), toSlot: dayEndSlot(lastDay) };
}

/** Do the half-open intervals `[aFrom, aTo)` and `[bFrom, bTo)` share any slot? */
export function intervalsOverlap(aFrom: number, aTo: number, bFrom: number, bTo: number): boolean {
  return aFrom < bTo && aTo > bFrom;
}

/** Intersection of two half-open intervals, or `null` when they are disjoint. */
export function clipInterval(
  fromSlot: number,
  toSlot: number,
  rangeFrom: number,
  rangeTo: number,
): SlotRange | null {
  const from = Math.max(fromSlot, rangeFrom);
  const to = Math.min(toSlot, rangeTo);
  return to > from ? { fromSlot: from, toSlot: to } : null;
}

/** Current time floored to the running 30-minute slot, in local wall-clock time. */
export function slotFromDate(date: Date): number {
  const slotInDay = Math.floor((date.getHours() * 60 + date.getMinutes()) / MINUTES_PER_SLOT);
  return slotFromLocalDate(date.getFullYear(), date.getMonth() + 1, date.getDate(), slotInDay);
}

export function nowSlot(now: Date = new Date()): number {
  return slotFromDate(now);
}

export function todayDayIndex(now: Date = new Date()): number {
  return dayFromLocalDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/** Local `Date` a slot starts at — used for native date pickers and tests. */
export function dateFromSlot(slot: number): Date {
  const { year, month, day } = localDateFromDay(dayOfSlot(slot));
  const inDay = slotOfDay(slot);
  return new Date(year, month - 1, day, Math.floor(inDay / 2), (inDay % 2) * MINUTES_PER_SLOT, 0, 0);
}

/** Rounds a slot count to whole minutes. Convenience for statistics output. */
export function slotsToMinutes(slots: number): number {
  return slots * MINUTES_PER_SLOT;
}
